import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Volume2,
  ArrowLeft,
  CheckCircle,
  XCircle
} from 'lucide-react';
import FloatingBackButton from './FloatingBackButton';
import { useSupabaseAudio } from '../hooks/useSupabaseAudio';
import {  useUserProgress, useConfirmationDialog } from '../hooks/hooks';
import { WordData, LearnModeProps, LearnedWord, LanguageCode, deserializeUserProgress, UserProgress, AppMode } from '../types/types';
import { shuffleArray } from '../utils/utils';
import { getLanguageData } from '../data/languages';
import { useTheme } from './ThemeContext';
import './LearnMode.css';
import TribalBorder from './TribalBorder';
import ProgressPie from './ProgressPie';
import { useSummaryAudio } from '../utils/summaryAudio';
import { cleanParentheses } from '../utils/cleanParentheses';
import { getWordTranslation, getWordExplanation, getWordExample } from '../utils/localizedContent';
import { displayCategoryLabel } from '../utils/categoryLabels';
import XPAnimation from './XPAnimation';
import StreakAnimation from './StreakAnimation';
import ConfirmationModal from './ConfirmationModal';

import SummaryScreen from './SummaryScreen';
import { getRandomCongrats, getRandomProverb, getSentierName } from '../data/summaryData';
import { Illustration } from './common/Picto';

const WORDS_PER_LESSON = 5;
const TESTS_PER_WORD = 3;
const XP_PER_WORD = 10;

interface LearnModeExtendedProps extends LearnModeProps {
  languageCode: LanguageCode;
  onAnswer?: (correct: boolean) => void;
  words?: Record<string, Record<string, WordData>>;
  isLoading?: boolean;
  sessionLearnedWords: Set<string>;
  setMode: React.Dispatch<React.SetStateAction<AppMode>>;
  isMobileView?: boolean;
  onSessionComplete?: () => void;
  onLessonSummary?: () => void;
  saveProgressOnSummary?: (progress: UserProgress, context?: any) => Promise<void>;
  userProgress?: UserProgress;
  wordRange?: { start: number; end: number };
  onShare?: () => void;
}




const LearnMode: React.FC<LearnModeExtendedProps> = ({
  languageCode,
  category,
  subcategory,
  onBackToCategories,
  onGameComplete,

  onWordLearned,
  onWordsLearned,

  resetToken,
  words,
  isMobileView,
  onAnswer,

  saveProgressOnSummary,
  wordRange,
  onNextCategory,
  onShare,
  onLessonSummary,
}) => {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const { playWord } = useSupabaseAudio(languageCode);
  const languageData = getLanguageData(languageCode);
  const { userProgress, updateProgress, addRecentlyLearnedWords, getFullWordKey } = useUserProgress(languageCode, category, resetToken);
  const { isOpen, open, close } = useConfirmationDialog(false);

  // États principaux
  const [currentWords, setCurrentWords] = useState<[string, WordData][]>([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [mode, setMode] = useState<'preview' | 'test' | 'summary'>('preview');
  const [testQueue, setTestQueue] = useState<string[]>([]);
  const [testScores, setTestScores] = useState<Record<string, number>>({});
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [isLessonComplete, setIsLessonComplete] = useState(false);
  const [stableOptions, setStableOptions] = useState<string[]>([]);
  const [showExitConfirmation, setShowExitConfirmation] = useState<boolean>(false);

  // États pour gérer la sauvegarde de manière stable
  const [shouldSave, setShouldSave] = useState(false);
  const [saveCompleted, setSaveCompleted] = useState(false);
  const [isProcessingSummary, setIsProcessingSummary] = useState(false);

  // ✅ NOUVEAU : États pour la gestion XP en deux étapes
  const [summaryStep, setSummaryStep] = useState<'results' | 'xp'>('results');
  const [showXPAnimation, setShowXPAnimation] = useState(false);
  const [showWordLearnedAnim, setShowWordLearnedAnim] = useState(false);
  const [xpGained, setXpGained] = useState(0);

  // Stabilise le proverbe et l'encouragement — recalculés uniquement au changement de mode
  const summaryCongrats = useMemo(() => getRandomCongrats(languageCode), [mode]);
  const summaryProverb  = useMemo(() => getRandomProverb(languageCode),  [mode]);

  // Mots définitivement appris VS temporairement maîtrisés
  const learnedWordsRef = useRef(new Set<string>());
  const [tempMasteredWords, setTempMasteredWords] = useState<Set<string>>(new Set());
  const [sessionLearnedWords, setSessionLearnedWords] = useState<Set<string>>(new Set());
  
  // Ref pour les mots temporairement maîtrisés
  const tempMasteredWordsRef = useRef<Set<string>>(new Set());
  
  // Ref pour figer les mots de la session une fois commencée
  const sessionWordsRef = useRef<[string, WordData][]>([]);
  const sessionStartedRef = useRef(false);
  
  // Ref pour dédupliquer l'auto-play audio en preview
  const lastAutoPlayedRef = useRef<{ index: number; audio: string } | null>(null);

  // Refs pour stabiliser les données
  const testScoresRef = useRef<Record<string, number>>({});
  const currentWordsRef = useRef<[string, WordData][]>([]);
  
  const storageKey = `${languageCode}-${category}-completed`;


  const { playSummaryAudio } = useSummaryAudio();

 
const getCategoryLearnedWords = useCallback((targetCategory: string): Set<string> => {
    const categoryLearnedWords = new Set<string>();
   
    
    try {
      // ✅ PRIORITÉ 1: userProgress.recentlyLearnedWords (SOURCE PRINCIPALE)
      if (userProgress?.recentlyLearnedWords && Array.isArray(userProgress.recentlyLearnedWords)) {
      
        
        userProgress.recentlyLearnedWords.forEach(item => {
          // Vérifier catégorie ET que ce n'est pas de la grammaire
          if (item.category === targetCategory && item.category !== 'Grammaire') {
            categoryLearnedWords.add(item.word);
            const cleanWord = item.word.split('(')[0].trim();
            categoryLearnedWords.add(cleanWord);
          }
        });
       
      }
      
      // ✅ PRIORITÉ 2: userProgress.learnedWords (BACKUP)
      if (userProgress?.learnedWords && userProgress.learnedWords instanceof Set) {
        Array.from(userProgress.learnedWords).forEach(word => {
          // Ajouter seulement si ce n'est pas un mot de grammaire
          // (heuristique: les mots de grammaire ont souvent des formats spéciaux)
          const recentWord = userProgress.recentlyLearnedWords?.find(r => r.word === word);
          if (recentWord?.category === targetCategory && recentWord.category !== 'Grammaire') {
            categoryLearnedWords.add(word);
            const cleanWord = word.split('(')[0].trim();
            categoryLearnedWords.add(cleanWord);
          }
        });
      }
      
      // ✅ 3. Fallback: localStorage catégorie
      const categoryLearnedKey = `${languageCode}-${targetCategory}-learnedWords`;
      const savedCategoryLearned = localStorage.getItem(categoryLearnedKey);

      if (savedCategoryLearned) {
        const categoryWords = JSON.parse(savedCategoryLearned) as string[];
        categoryWords.forEach(word => {
          categoryLearnedWords.add(word);
          const cleanWord = word.split('(')[0].trim();
          categoryLearnedWords.add(cleanWord);
        });
      }

      // ✅ 4. Fallback: liste globale héritée (allLearnedWords) — filtrée par la catégorie cible
      const globalLearnedKey = `${languageCode}-allLearnedWords`;
      const savedGlobalLearned = localStorage.getItem(globalLearnedKey);
      if (savedGlobalLearned) {
        const globalWords = JSON.parse(savedGlobalLearned) as string[];
        const categoryData = (languageData.categories[targetCategory] || {}) as Record<string, unknown>;
        const categoryWordSet = new Set(
          Object.keys(categoryData)
            .filter(w => !w.includes('_'))
            .flatMap(w => [w, w.split('(')[0].trim(), w.toLowerCase(), w.split('(')[0].trim().toLowerCase()])
        );
        globalWords.forEach(word => {
          const clean = word.split('(')[0].trim();
          if (categoryWordSet.has(word) || categoryWordSet.has(clean) ||
              categoryWordSet.has(word.toLowerCase()) || categoryWordSet.has(clean.toLowerCase())) {
            categoryLearnedWords.add(word);
            categoryLearnedWords.add(clean);
          }
        });
      }

    } catch (error) {
}
    
    return categoryLearnedWords;
  }, [languageCode, userProgress, languageData.categories]);

  // 🔧 NOUVEAU : Fonction pour sauvegarder les mots appris PAR CATÉGORIE
  const saveCategoryLearnedWords = useCallback((targetCategory: string, wordsToAdd: string[]) => {
    const categoryLearnedKey = `${languageCode}-${targetCategory}-learnedWords`;
    
    try {
      // Charger la liste existante
      let categoryLearnedWords: string[] = [];
      const savedCategoryLearned = localStorage.getItem(categoryLearnedKey);
      
      if (savedCategoryLearned) {
        categoryLearnedWords = JSON.parse(savedCategoryLearned) as string[];
      }
      
      // Ajouter les nouveaux mots sans doublons
      wordsToAdd.forEach(word => {
        if (!categoryLearnedWords.includes(word)) {
          categoryLearnedWords.push(word);
        
        }
      });
      
      // Sauvegarder la liste mise à jour
      localStorage.setItem(categoryLearnedKey, JSON.stringify(categoryLearnedWords));
     
      
      return categoryLearnedWords;
    } catch (error) {
return [];
    }
  }, [languageCode]);

  // 🔧 NOUVEAU : Migration automatique des données existantes
  const migrateToCategories = useCallback(() => {
    const migrationKey = `${languageCode}-migration-to-categories-completed`;
    
    if (localStorage.getItem(migrationKey)) {
     
      return;
    }
    
   
    
    try {
      // Charger l'ancienne liste globale
      const globalLearnedKey = `${languageCode}-allLearnedWords`;
      const savedGlobalLearned = localStorage.getItem(globalLearnedKey);
      
      if (savedGlobalLearned) {
        const globalWords = JSON.parse(savedGlobalLearned) as string[];
       
        
        // Répartir les mots par catégorie
        Object.keys(languageData.categories).forEach(categoryName => {
          const categoryWords = languageData.categories[categoryName];
          if (!categoryWords) return;
          
          const categoryLearnedWords: string[] = [];
          
          Object.keys(categoryWords).forEach(word => {
            if (word.includes('_')) return;
            
            const cleanWord = cleanParentheses(word);
            
            // Si le mot est dans la liste globale, l'ajouter à cette catégorie
            if (globalWords.includes(cleanWord) || globalWords.includes(word)) {
              if (!categoryLearnedWords.includes(cleanWord)) {
                categoryLearnedWords.push(cleanWord);
              }
            }
          });
          
          // Sauvegarder pour cette catégorie
          if (categoryLearnedWords.length > 0) {
            const categoryLearnedKey = `${languageCode}-${categoryName}-learnedWords`;
            localStorage.setItem(categoryLearnedKey, JSON.stringify(categoryLearnedWords));
          
          }
        });
        
        // Marquer la migration comme terminée
        localStorage.setItem(migrationKey, 'true');
     
      }
    } catch (error) {
}
  }, [languageCode, languageData.categories]);

  // ✅ NOUVEAU : Fonction pour passer à l'étape XP
  const handleShowXP = useCallback(() => {
   
    
    const totalXPGained = Object.values(testScoresRef.current).filter(score => score >= TESTS_PER_WORD).length * XP_PER_WORD;
    
    if (totalXPGained > 0) {
      setSummaryStep('xp');
      setXpGained(totalXPGained);
      
      setTimeout(() => {
        setShowXPAnimation(true);
      }, 300);
    } else {
      setSummaryStep('xp');
    }
  }, []);

  // ✅ NOUVEAU : Fonction pour réinitialiser les états du résumé
  const resetSummaryState = useCallback(() => {
    setSummaryStep('results');
    setShowXPAnimation(false);
    setShowWordLearnedAnim(false);
    setXpGained(0);
    setShouldSave(false);
    setSaveCompleted(false);
    setIsProcessingSummary(false);
  }, []);

  // Synchroniser les refs avec les states
  useEffect(() => {
    testScoresRef.current = testScores;
  }, [testScores]);

  useEffect(() => {
    currentWordsRef.current = currentWords;
  }, [currentWords]);

  // Nettoyage lors du changement de mode
  useEffect(() => {
    if (mode === 'test') {
    
      setSelectedAnswer(null);
    }
  }, [mode]);

  // Initialisation et chargement
  useEffect(() => {
    if (isLessonComplete) {
      localStorage.setItem(storageKey, 'true');
    }
  }, [isLessonComplete, storageKey]);

  useEffect(() => {
    const isCompleted = localStorage.getItem(storageKey) === 'true';
    if (isCompleted) {
      setIsLessonComplete(true);
    }
  }, [storageKey]);

  // 🔧 MODIFIÉ : Initialisation de learnedWordsRef avec stockage par catégorie
  useEffect(() => {
   
    const categoryLearnedWords = getCategoryLearnedWords(category);
    learnedWordsRef.current = new Set([...categoryLearnedWords]);
   
  }, [category, getCategoryLearnedWords]);

  useEffect(() => {
    if (resetToken) {
    
      learnedWordsRef.current = new Set();
      setTempMasteredWords(new Set());
      sessionStartedRef.current = false;
      sessionWordsRef.current = [];
      resetSummaryState(); // ✅ NOUVEAU
    }
  }, [resetToken, resetSummaryState]);

  // 🔧 MODIFIÉ : Mémoriser la liste des mots appris par catégorie
  const stableLearnedWords = useMemo(() => {
    const categoryLearnedWords = getCategoryLearnedWords(category);
    const allLearned = [
      ...Array.from(learnedWordsRef.current),
      ...Array.from(categoryLearnedWords)
    ];
    return allLearned.sort().join(',');
  }, [category, getCategoryLearnedWords]);

  // Effectuer la migration au montage
  useEffect(() => {
    migrateToCategories();
  }, [migrateToCategories]);

  const generateStableOptions = useCallback((wordToTest: string, allCurrentWords: [string, WordData][]) => {
    const correctWord = allCurrentWords.find(([word]) => word === wordToTest);
    if (!correctWord) return [];

    // Localizing here (rather than at render) keeps the question, the
    // answer buttons and the correction panel all consistent — they all
    // derive from this same localized value, so they can never disagree.
    const correctTranslation = getWordTranslation(correctWord[1], i18n.language);
    const sessionWrong = allCurrentWords
      .filter(([word]) => word !== wordToTest)
      .map(([_, data]) => getWordTranslation(data, i18n.language));

    let wrongOptions = shuffleArray(sessionWrong).slice(0, 3);

    // Pad with translations from the full category when session has fewer than 4 words
    if (wrongOptions.length < 3) {
      const usedTranslations = new Set([correctTranslation, ...wrongOptions]);
      const fullCategoryWords = Object.values(
        (languageData.categories[category] as Record<string, WordData>) ?? {}
      );
      const extra = shuffleArray(
        fullCategoryWords
          .map(d => getWordTranslation(d, i18n.language))
          .filter(t => t && !usedTranslations.has(t))
      ).slice(0, 3 - wrongOptions.length);
      wrongOptions = [...wrongOptions, ...extra];
    }

    return shuffleArray([correctTranslation, ...wrongOptions]);
  }, [languageData, category, i18n.language]);

  const startTest = useCallback(() => {
 

  
  if (currentWords.length === 0) {
return;
  }
  
  // Réinitialisation au début du test
 
  setSelectedAnswer(null);
  
  // Figer les mots de la session
  sessionWordsRef.current = [...currentWords];
  sessionStartedRef.current = true;
  
  // Initialiser la ref des scores
  testScoresRef.current = { ...testScores };
  
  setMode('test');
  
  // Créer la file de test
  const queue = currentWords.flatMap(([word]) => {
    const alreadyCorrect = testScores[word] || 0;
    const testsNeeded = Math.max(0, TESTS_PER_WORD - alreadyCorrect);
    return Array(testsNeeded).fill(word);
  });
  
  const shuffledQueue = shuffleArray(queue);

  setTestQueue(shuffledQueue);
  
  if (shuffledQueue.length > 0) {
    const firstWord = shuffledQueue[0];
    const options = generateStableOptions(firstWord, currentWords);
    setStableOptions(options);
    
  
  }
  
  // Sauvegarde temporaire pour reprise de session
  const sessionData = {
    words: currentWords.map(([word]) => word),
    scores: testScores,
    timestamp: Date.now(),
    mode: 'test',
    currentIndex: 0,
  };
  
  try {
    const inProgressKey = `${languageCode}-${category}-inProgress`;
    localStorage.setItem(inProgressKey, JSON.stringify(sessionData));
  } catch (error) {
}
}, [currentWords, generateStableOptions, testScores, playWord, languageCode, category]);

  const handleNextWord = useCallback(async () => {
    if (currentWordIndex < currentWords.length - 1) {
      setCurrentWordIndex(prev => prev + 1);
    } else if (mode === 'preview') {
      startTest();
    }
  }, [currentWordIndex, currentWords.length, mode, startTest]);


const handleSummaryComplete = useCallback(async () => {

  
  const currentTestScores = testScoresRef.current;
  const currentWordsData = sessionWordsRef.current.length > 0 ? sessionWordsRef.current : currentWordsRef.current;
  const tempMasteredWordsSet = tempMasteredWordsRef.current || new Set();
  

  
  try {
    // Nettoyer les sessions temporaires
    const storageKeysToClean = [
      `${languageCode}-${category}-inProgress`,
      `${languageCode}-${category}-temp`,
      `${languageCode}-${category}-session`
    ];
    
    storageKeysToClean.forEach(key => {
      localStorage.removeItem(key);
     
    });

    // IDENTIFIER les mots RÉELLEMENT maîtrisés selon les scores finaux
    const definitiveMasteredWords: string[] = [];
    const masteredWordsData: LearnedWord[] = [];
    
    Object.entries(currentTestScores).forEach(([word, score]) => {
      if (score >= TESTS_PER_WORD) {
        const cleanWord = cleanParentheses(word);
        definitiveMasteredWords.push(cleanWord);
        
        const wordData = currentWordsData.find(([w]) => w === word)?.[1];
        if (wordData) {
          masteredWordsData.push({
            word: cleanWord,
            category: category,
            subcategory: subcategory || '',
            language: languageCode,
            timestamp: Date.now(),
            audio: wordData.audio || '',
            translation: wordData.translation || '',
            explanation: wordData.explanation || '',
            example: wordData.example || ''
          });
        }
      }
    });
    
    if (definitiveMasteredWords.length > 0) {
      // Record daily activity for heatmap
      try {
        const _d = new Date();
        const todayKey = `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, '0')}-${String(_d.getDate()).padStart(2, '0')}`;
        const raw = localStorage.getItem(`${languageCode}-daily-activity`);
        const map: Record<string, number> = raw ? JSON.parse(raw) : {};
        map[todayKey] = (map[todayKey] ?? 0) + definitiveMasteredWords.length;
        localStorage.setItem(`${languageCode}-daily-activity`, JSON.stringify(map));
      } catch {}

       playSummaryAudio();
      // 🔧 Sauvegarder dans la catégorie spécifique (localStorage)
      const categoryLearnedWords = saveCategoryLearnedWords(category, definitiveMasteredWords);
      
      // Ajouter aux références définitives EN MÉMOIRE
      definitiveMasteredWords.forEach(word => {
        learnedWordsRef.current.add(word);
        userProgress.learnedWords.add(word);
       
      });
      
      // Préparer la progression finale
      const finalProgress: UserProgress = {
        ...userProgress,
        learnedWords: new Set([
          ...Array.from(userProgress.learnedWords),
          ...definitiveMasteredWords
        ]),
        recentlyLearnedWords: [
          ...userProgress.recentlyLearnedWords,
          ...masteredWordsData
        ]
      };
   
      
      // ✅ SAUVEGARDE UNIFIÉE avec données spécifiques LearnMode
      if (saveProgressOnSummary) {
       
        
        const totalScore = Object.values(currentTestScores).reduce((sum: number, score: number) => {
          return sum + score;
        }, 0);
        
        const maxScore = Object.keys(currentTestScores).length * TESTS_PER_WORD;
        const accuracy = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;
        
       await saveProgressOnSummary(finalProgress, {
  mode: 'learn',
  sessionStats: {
    wordsLearned: definitiveMasteredWords.length,
    totalScore: totalScore,
    maxScore: maxScore,
    accuracy: accuracy
  },
  description: `Fin de session d'apprentissage ${category} - ${definitiveMasteredWords.length} mots maîtrisés`,
  includeGoogleDrive: true, // ✅ Important
  learnSpecificData: {
    category: category,
    subcategory: subcategory || '',
    masteredWords: masteredWordsData.map(w => ({
      word: w.word,
      data: {
        translation: w.translation,
        explanation: w.explanation,
        example: w.example,
        audio: w.audio || ''
      },
      category: w.category,
      subcategory: w.subcategory,
      timestamp: w.timestamp
    })),
    date: Date.now(),
    totalScore: totalScore,
    maxScore: maxScore,
    xpGained: definitiveMasteredWords.length * 10
  }
});

        
      
      } else {
}
      
      // ENVOI DES MOTS VERS LA RÉVISION
     
      
      try {
        onWordsLearned(masteredWordsData);
     
        
        // Faire tous les appels updateProgress/onWordLearned à la fin
        Object.entries(currentTestScores).forEach(([word, score]) => {
          const wordData = currentWordsData.find(([w]) => w === word)?.[1];
          if (wordData) {
            for (let i = 0; i < score; i++) {
              updateProgress(word, true, subcategory);
              onAnswer?.(true);
            }
            if (score >= TESTS_PER_WORD) {
              onWordLearned(word, true);
             
            }
          }
        });
        
      } catch (revisionError) {
}
      
      // ✅ Mise à jour des statistiques globales optionnelle
      try {
        const globalStatsKey = `${languageCode}-category-stats`;
        let globalStats: Record<string, number> = {};
        const savedGlobalStats = localStorage.getItem(globalStatsKey);
        
        if (savedGlobalStats) {
          globalStats = JSON.parse(savedGlobalStats);
        }
        
        globalStats[category] = categoryLearnedWords.length;
        localStorage.setItem(globalStatsKey, JSON.stringify(globalStats));
    
      } catch (error) {
}
    } else {
 
    }
    
  
    
    // Nettoyer explicitement la clé des mots en attente
    const pendingWordsKey = `${languageCode}-${category}-pendingWords`;
    localStorage.removeItem(pendingWordsKey);
   
    
    // Nettoyer les mots temporaires
    setTempMasteredWords(new Set());
  
    
    // Marquer la sauvegarde comme terminée
    setSaveCompleted(true);
    setIsProcessingSummary(false);
    
   
    
  } catch (error) {
setIsProcessingSummary(false);
  }
  

}, [
  category,
  languageCode,
  subcategory,
  onWordsLearned,
  saveProgressOnSummary,
  updateProgress,
  onAnswer,
  onWordLearned,
  saveCategoryLearnedWords,
  playSummaryAudio
]);

useEffect(() => {
  const handleLearnProgressSynced = (event: CustomEvent) => {
    const { sessions, language, totalSessions, source } = event.detail;
    
  
    
    if (language === languageCode) {
     
      
      // Créer un map pour grouper les mots par catégorie
      const wordsByCategory: Record<string, string[]> = {};
      
      sessions.forEach((session: any, sessionIndex: number) => {
       
        
        if (session.masteredWords && Array.isArray(session.masteredWords)) {
          const categoryName = session.category;
          if (!wordsByCategory[categoryName]) {
            wordsByCategory[categoryName] = [];
          }
          
          session.masteredWords.forEach((wordData: any) => {
            if (wordData.word && typeof wordData.word === 'string') {
              const cleanWord = cleanParentheses(wordData.word);
              if (!wordsByCategory[categoryName].includes(cleanWord)) {
                wordsByCategory[categoryName].push(cleanWord);
              }
            }
          });
        }
      });
      
      // Sauvegarder chaque catégorie dans localStorage
      Object.entries(wordsByCategory).forEach(([categoryName, words]) => {
        const categoryKey = `${language}-${categoryName}-learnedWords`;
        
        // Fusionner avec les mots existants
        const existingWords = localStorage.getItem(categoryKey);
        const existingWordsArray = existingWords ? JSON.parse(existingWords) : [];
        
        const mergedWords = [...new Set([...existingWordsArray, ...words])];
        localStorage.setItem(categoryKey, JSON.stringify(mergedWords));
        
      
        
        // Si c'est la catégorie actuelle, mettre à jour l'état
        if (categoryName === category) {
          const categoryLearnedWords = new Set(mergedWords);
          // Mettre à jour learnedWordsRef si nécessaire
          mergedWords.forEach(word => {
            learnedWordsRef.current.add(word);
          });
       
        }
      });
      
    
    }
  };

  window.addEventListener('learnProgressSynced', handleLearnProgressSynced as EventListener);
  
  return () => {
    window.removeEventListener('learnProgressSynced', handleLearnProgressSynced as EventListener);
  };
}, [languageCode, category]);




  // useEffect dédié pour déclencher la sauvegarde une seule fois
  useEffect(() => {
    if (shouldSave && mode === 'summary' && !saveCompleted && !isProcessingSummary) {
    
      setIsProcessingSummary(true);
      
      const performSave = async () => {
        try {
          await handleSummaryComplete();
          setShouldSave(false);
        } catch (error) {
setIsProcessingSummary(false);
        }
      };
      
      performSave();
    }
  }, [shouldSave, mode, saveCompleted, isProcessingSummary, handleSummaryComplete]);

  // Signale une fois par écran de résumé de leçon (pas à chaque re-render) :
  // permet à App.tsx de proposer une pub interstitielle entre deux leçons,
  // pas seulement à la toute fin d'une catégorie.
  const lessonSummaryNotifiedRef = useRef(false);
  useEffect(() => {
    if (mode === 'summary') {
      if (!lessonSummaryNotifiedRef.current) {
        lessonSummaryNotifiedRef.current = true;
        onLessonSummary?.();
      }
    } else {
      lessonSummaryNotifiedRef.current = false;
    }
  }, [mode, onLessonSummary]);

  // ✅ MODIFIÉ : handleAnswer simplifié sans feedback
  const handleAnswer = useCallback((answer: string) => {
    if (mode !== 'test' || testQueue.length === 0) return;

    const currentWord = testQueue[0];
    const wordData = currentWords.find(([word]) => word === currentWord)?.[1];
    if (!wordData) return;

    const displayWord = cleanParentheses(currentWord);
    // Must match the same localized value handed out as the correct option
    // by generateStableOptions — comparing against the raw French field here
    // would break matching once an English translation is filled in.
    const isCorrect = answer === getWordTranslation(wordData, i18n.language);
    
    // ✅ GARDER : Code couleur sur les boutons
    setSelectedAnswer(answer);

 

    // CALCULER les nouveaux scores dans la REF SEULEMENT
    if (isCorrect) {
      const newScore = (testScoresRef.current[currentWord] || 0) + 1;
      testScoresRef.current[currentWord] = newScore;
     
      
      if (newScore >= TESTS_PER_WORD) {
        const cleanWord = cleanParentheses(currentWord);
        const currentTempMastered = tempMasteredWordsRef.current || new Set();
        currentTempMastered.add(cleanWord);
        tempMasteredWordsRef.current = currentTempMastered;
        setShowWordLearnedAnim(true);
      }
    } else {
    
    }
   

    // ✅ SIMPLIFIER : Délai réduit et nettoyage simplifié
    setTimeout(() => {
      setSelectedAnswer(null); // Nettoyer seulement la sélection
      
      const newQueue = testQueue.slice(1);
      
      if (newQueue.length === 0) {
        // FIN DU TEST
      
        setTestScores({ ...testScoresRef.current });
        setMode('summary');
        
        // Marquer qu'une sauvegarde doit avoir lieu
        setShouldSave(true);
        setSaveCompleted(false);
        setIsProcessingSummary(false);
      } else {
        // QUESTION SUIVANTE
       
        setTestQueue(newQueue);
        
        const nextWord = newQueue[0];
        const newOptions = generateStableOptions(nextWord, currentWords);
        setStableOptions(newOptions);

        const nextWordData = currentWords.find(([word]) => word === nextWord)?.[1];
        if (nextWordData?.audio) {
          playWord(nextWordData.audio).catch(() => {});
        }
      }
    }, 1000); // ✅ RÉDUIT : 1 seconde au lieu de 1.5
  }, [
    mode,
    testQueue,
    currentWords,
    generateStableOptions,
    playWord,
    i18n.language
  ]);

  const handleExitConfirm = useCallback(() => {
   
    
    setShowExitConfirmation(false);
    
    // Nettoyage complet sans aucune sauvegarde
    const tempMasteredWordsSet = tempMasteredWordsRef.current || new Set();
   
    
    // Nettoyer userProgress des mots temporaires
    tempMasteredWordsSet.forEach(word => {
      const cleanWord = cleanParentheses(word);
      const variations = [word, cleanWord, word.toLowerCase(), cleanWord.toLowerCase()];
      
      variations.forEach(variant => {
        if (userProgress.learnedWords.has(variant)) {
          if (!learnedWordsRef.current.has(variant)) {
            userProgress.learnedWords.delete(variant);
        
          }
        }
      });
    });
    
    // Supprimer les sessions temporaires
    const storageKeys = [
      `${languageCode}-${category}-inProgress`,
      `${languageCode}-${category}-sessionLearned`,
      `${languageCode}-${category}-temp`,
      `${languageCode}-${category}-pendingWords`,
      `${languageCode}-${category}-session`,
      `${languageCode}-${category}-progress`
    ];
    
    storageKeys.forEach(key => {
      localStorage.removeItem(key);
     
    });
    
    // Réinitialiser les états
    setMode('preview');
    setCurrentWords([]);
    setCurrentWordIndex(0);
    setTestQueue([]);
    setTestScores({});
    setSelectedAnswer(null);
    setStableOptions([]);
    setSessionLearnedWords(new Set());
    setTempMasteredWords(new Set());
    setShouldSave(false);
    setSaveCompleted(false);
    setIsProcessingSummary(false);
    setShowXPAnimation(false);
    setXpGained(0);
    sessionStartedRef.current = false;
    sessionWordsRef.current = [];
    tempMasteredWordsRef.current = new Set();
    
    // ✅ NOUVEAU : Reset des états du résumé
    resetSummaryState();
    
  
    
    if (onBackToCategories) {
      onBackToCategories();
    }
  }, [
    languageCode, 
    category, 
    onBackToCategories, 
    userProgress.learnedWords,
    resetSummaryState
  ]);

  const handleBackButton = useCallback(() => {
   
    
    if (mode === 'summary' || isLessonComplete) {

      
      // ✅ NOUVEAU : Réinitialiser les états du résumé lors de la sortie
      resetSummaryState();
      
      if (onBackToCategories) {
        onBackToCategories();
      }
      return;
    }
    
    if (currentWords.length > 0) {
   
      setShowExitConfirmation(true);
    } else {
     
      if (onBackToCategories) {
        onBackToCategories();
      }
    }
  }, [mode, isLessonComplete, currentWords.length, onBackToCategories, resetSummaryState]);

  // 🔧 MODIFIÉ : allWords useMemo avec stockage par catégorie
  const allWords = useMemo(() => {
    // PROTECTION : Si une session est active, ne pas recalculer
    if (sessionStartedRef.current || mode === 'test' || mode === 'summary') {
    
      return [];
    }

    let categoryData: Record<string, WordData> = {};

    if (words && subcategory && words[subcategory]) {
      categoryData = words[subcategory];
    } else {
      categoryData = (languageData.categories[category] || {}) as Record<string, WordData>;
    }

    // Extraire les entrées valides dans l'ordre du dictionnaire
    let allEntries = Object.entries(categoryData).filter(([w]) => w && !w.includes('_')) as [string, WordData][];

    // Restreindre à la plage de mots de cette partie si définie
    if (wordRange) {
      allEntries = allEntries.slice(wordRange.start, wordRange.end);
    }

    const categoryLearnedWords = getCategoryLearnedWords(category);

    const entries = allEntries.filter(([word]) => {
      const cleanWord = word.split('(')[0].trim();
      const isLearnedInThisCategory = categoryLearnedWords.has(cleanWord) || categoryLearnedWords.has(word);
      return !isLearnedInThisCategory;
    });

    return entries;
  }, [
    category,
    languageData.categories,
    words,
    subcategory,
    mode,
    wordRange,
    getCategoryLearnedWords
  ]);

  const getRemainingWords = useCallback((newLearnedWords: string[] = []): [string, WordData][] => {
    // 🔧 MODIFIÉ : Utiliser les mots appris de la catégorie + les nouveaux
    const categoryLearnedWords = getCategoryLearnedWords(category);
    const learnedWordsSet = new Set([
      ...Array.from(categoryLearnedWords),
      ...Array.from(tempMasteredWords),
      ...newLearnedWords.map(word => cleanParentheses(word))
    ]);
    
    return allWords.filter(([word]) => {
      const cleanWord = cleanParentheses(word);
      return !learnedWordsSet.has(cleanWord) && !learnedWordsSet.has(word);
    });
  }, [allWords, category, getCategoryLearnedWords, tempMasteredWords]);

  const loadNextWords = useCallback((newLearnedWords: string[] = []) => {
    const storageKey = `${languageCode}-${category}-inProgress`;
  
   
    
    const savedProgress = localStorage.getItem(storageKey);
    
    // Charger session uniquement si elle est récente et valide
    if (savedProgress) {
      try {
        const { 
          words, 
          scores, 
          timestamp, 
          mode: savedMode = 'preview',
          currentIndex = 0 
        } = JSON.parse(savedProgress);

        // Session récente (moins de 24h)
        if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
          let categoryData: Record<string, WordData> = {};
          
          if (words && subcategory && words[subcategory]) {
            categoryData = words[subcategory];
          } else {
            categoryData = (languageData.categories[category] || {}) as Record<string, WordData>;
          }

          const alreadyLearned = getCategoryLearnedWords(category);
          const restoredWords = words
            .filter((word: string) => {
              const clean = cleanParentheses(word);
              return !alreadyLearned.has(word) && !alreadyLearned.has(clean);
            })
            .map((word: string) => [word, categoryData[word]] as [string, WordData]);

          localStorage.removeItem(storageKey);

          if (restoredWords.length > 0) {
            setCurrentWords(restoredWords);
            setTestScores(scores);
            setMode(savedMode);
            setCurrentWordIndex(currentIndex);

            if (savedMode === 'test') {
              const queue = restoredWords.flatMap(([word]: [string, WordData]) =>
                Array(TESTS_PER_WORD - (scores[word] || 0)).fill(word)
              );
              const shuffledQueue: string[] = shuffleArray(queue) as string[];
              setTestQueue(shuffledQueue);

              if (shuffledQueue.length > 0) {
                const firstWord = shuffledQueue[0];
                const options = generateStableOptions(firstWord, restoredWords);
                setStableOptions(options);
              }
            }

            return;
          }
          // restoredWords vide : tous déjà appris → charger les mots restants ci-dessous
        }
      } catch (error) {
}
    }

    // Charger de nouveaux mots
    const remainingWords = getRemainingWords(newLearnedWords);
    const nextWords = remainingWords.slice(0, WORDS_PER_LESSON);

    if (nextWords.length > 0) {
     
      
      setCurrentWords(nextWords);
      setCurrentWordIndex(0);
      setMode('preview');
      const initialScores = Object.fromEntries(nextWords.map(([word]) => [word, 0]));
      setTestScores(initialScores);
      testScoresRef.current = initialScores;
      setTestQueue([]);
      setStableOptions([]);
      setIsLessonComplete(false);
      setTempMasteredWords(new Set());
      setShouldSave(false);
      setSaveCompleted(false);
      setIsProcessingSummary(false);
      setShowXPAnimation(false);
      setXpGained(0);
      sessionStartedRef.current = false;
      sessionWordsRef.current = [];
      tempMasteredWordsRef.current = new Set();
      
      // ✅ NOUVEAU : Reset des états du résumé
      resetSummaryState();
      
      const newSession = {
        words: nextWords.map(([word]) => word),
        scores: {},
        timestamp: Date.now(),
        mode: 'preview',
        currentIndex: 0
      };
      localStorage.setItem(storageKey, JSON.stringify(newSession));
    } else {
      setIsLessonComplete(true);
    }
  }, [
    languageCode, 
    category, 
    subcategory,
    words,
    getRemainingWords, 
    languageData.categories,
    generateStableOptions,
    resetSummaryState
  ]);

  const handleStartNewSession = useCallback(() => {
   
    
    // FORCER LA RÉINITIALISATION COMPLÈTE DES ÉTATS
 
    
    // ✅ NOUVEAU : Réinitialiser les états du résumé
    resetSummaryState();
    
    // 1. Réinitialiser tous les états liés aux mots
    setCurrentWords([]);
    setCurrentWordIndex(0);
    setTestQueue([]);
    setTestScores({});
    setSelectedAnswer(null);
    setStableOptions([]);
    
    // 2. Réinitialiser les états de session
    setSessionLearnedWords(new Set());
    setTempMasteredWords(new Set());
    
    // 3. Réinitialiser les états de sauvegarde (déjà fait dans resetSummaryState)
    
    // 4. Réinitialiser les états d'animation XP (déjà fait dans resetSummaryState)
    
    // 5. Réinitialiser les références
    sessionStartedRef.current = false;
    sessionWordsRef.current = [];
    tempMasteredWordsRef.current = new Set();
    testScoresRef.current = {};
    
    // 6. Forcer le mode preview AVANT de charger les nouveaux mots
  
    setMode('preview');
    
    // 7. Attendre que le changement de mode soit appliqué
    setTimeout(() => {
     
      loadNextWords();
    }, 100);
    
  }, [loadNextWords, resetSummaryState]);

 const handleGameComplete = useCallback(() => {

  
  // ✅ Réinitialiser les états du résumé
  resetSummaryState();
  
  // ✅ NOUVEAU : Marquer la catégorie comme terminée
  try {
    // Calculer le nombre total de mots dans la catégorie
    let categoryData: Record<string, WordData> = {};
    if (words && subcategory && words[subcategory]) {
      categoryData = words[subcategory];
    } else {
      categoryData = (languageData.categories[category] || {}) as Record<string, WordData>;
    }
    
    const totalWords = Object.keys(categoryData).filter(w => w && !w.includes('_')).length;
    const categoryLearnedWords = getCategoryLearnedWords(category);
    
    // Marquer comme terminée avec timestamp
    const categoryCompletedKey = `${languageCode}-${category}-completed`;
    localStorage.setItem(categoryCompletedKey, JSON.stringify({
      completed: true,
      completedAt: Date.now(),
      totalWords: totalWords,
      completedWords: categoryLearnedWords.size,
      lastSessionWords: Object.keys(testScoresRef.current).length
    }));
    
   
  } catch (error) {
}
  
  // ✅ Déclenche la logique de complétion côté App (XP/sync Supabase,
  // pub interstitielle, célébration) avant de quitter l'écran
  onGameComplete?.();

  // ✅ Navigation vers les catégories
  if (onBackToCategories) {
    onBackToCategories();
  }
}, [
  onBackToCategories,
  onGameComplete,
  resetSummaryState,
  category,
  languageCode,
  languageData.categories,
  getCategoryLearnedWords,
  words,
  subcategory
]);



  useEffect(() => {
    loadNextWords();
  }, [loadNextWords, resetToken]);

  // Nettoyage lors du changement de testQueue
  useEffect(() => {
    if (testQueue.length > 0) {
      setSelectedAnswer(null);
    }
  }, [testQueue]);

  // Chaque nouvel écran (mot, question, résumé) repart du haut : sinon la
  // page garde le défilement de l'écran précédent (la fiche du mot est plus
  // longue) et la question apparaît décalée, le mot coupé en haut.
  const currentTestWord = testQueue[0];
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [mode, currentWordIndex, currentTestWord]);

  // Effets de sauvegarde simplifiés
  useEffect(() => {
    if (mode === 'preview' && currentWords.length > 0) {
      // Sauvegarder uniquement l'état de navigation, pas les mots appris
      const dataToSave = {
        words: currentWords.map(([word]) => word),
        scores: {},
        timestamp: Date.now(),
        mode: mode,
        currentIndex: currentWordIndex
      };
      const storageKey = `${languageCode}-${category}-inProgress`;
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    }
  }, [mode, currentWords, currentWordIndex, languageCode, category]);

  useEffect(() => {
    if (mode === 'preview' && currentWords[currentWordIndex]) {
      const [, { audio }] = currentWords[currentWordIndex];
      if (audio) {
        const alreadyPlayed =
          lastAutoPlayedRef.current?.index === currentWordIndex &&
          lastAutoPlayedRef.current?.audio === audio;
        if (!alreadyPlayed) {
          lastAutoPlayedRef.current = { index: currentWordIndex, audio };
          playWord(audio).catch(() => {});
        }
      }
    }
  }, [mode, currentWordIndex, currentWords, playWord]);

  useEffect(() => {
    setSessionLearnedWords(new Set());
    setTempMasteredWords(new Set());
  }, [category, subcategory]);

  // Calculer les valeurs du résumé
  const currentTestScores = testScoresRef.current;
  const totalScore = Object.values(currentTestScores).reduce((sum: number, score: number) => {
    return sum + score;
  }, 0);
  const maxScore = Object.keys(currentTestScores).length * TESTS_PER_WORD;
  const accuracy = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;
  
  // Calculer remainingWords correctement pour le résumé
  const masteredWordsInSession = Object.entries(currentTestScores)
    .filter(([_, score]) => score >= TESTS_PER_WORD)
    .map(([word, _]) => cleanParentheses(word));
  
  // 🔧 MODIFIÉ : Calculer le nombre de mots restants avec stockage par catégorie
  let categoryData: Record<string, WordData> = {};
  if (words && subcategory && words[subcategory]) {
    categoryData = words[subcategory];
  } else {
    categoryData = (languageData.categories[category] || {}) as Record<string, WordData>;
  }
  
  // Tous les mots de la catégorie
  const allCategoryWords = Object.keys(categoryData).filter(word => 
    word && !word.includes('_')
  );
  
  // Mots déjà appris avant cette session (dans cette catégorie uniquement)
  const categoryLearnedWords = getCategoryLearnedWords(category);
  const previouslyLearnedWords = Array.from(categoryLearnedWords);
  
  // Mots appris dans cette session
  const newlyLearnedWords = masteredWordsInSession;
  
  // Calculer les mots restants
  const allLearnedWords = [...previouslyLearnedWords, ...newlyLearnedWords];
  const remainingWords = allCategoryWords.filter(word => {
    const cleanWord = cleanParentheses(word);
    return !allLearnedWords.some(learned => 
      cleanParentheses(learned) === cleanWord || learned === word
    );
  }).length;

  // Compter les mots réellement maîtrisés (score = 3/3)
  const masteredWordsCount = Object.values(currentTestScores).filter(score => score >= TESTS_PER_WORD).length;

  // Calculer l'XP gagné
  const totalXPGained = masteredWordsCount * XP_PER_WORD;

  // ✅ Interface - Mode résumé EN PREMIER POUR CORRIGER L'AFFICHAGE
  if (mode === 'summary') {
    const congrats = summaryCongrats;
    const proverb  = summaryProverb;

    const masteredEntries = Object.entries(currentTestScores).filter(([_, s]) => s >= TESTS_PER_WORD);
    const reviewEntries   = Object.entries(currentTestScores).filter(([_, s]) => s < TESTS_PER_WORD);

    const primaryAction = () => {
      resetSummaryState();
      if (remainingWords > 0) handleStartNewSession();
      else handleGameComplete();
    };

    return (
      <SummaryScreen
        theme={theme}
        label={t('learnMode.summary.lessonDoneLabel')}
        congrats={congrats}
        stats={[
          { value: `+${totalXPGained}`, label: t('learnMode.summary.xp') },
          { value: `${masteredWordsCount}/${Object.keys(currentTestScores).length}`, label: t('learnMode.summary.mastered') },
          { value: `${accuracy}%`, label: t('learnMode.summary.accuracy') },
        ]}
        proverb={proverb}
        primaryBtn={{
          label: remainingWords > 0
            ? t('learnMode.summary.continueLearning')
            : t('learnMode.summary.finishCategory', { category }),
          onClick: primaryAction,
        }}
        secondaryBtn={{
          label: t('learnMode.summary.backToCategories'),
          onClick: () => { resetSummaryState(); onBackToCategories?.(); },
        }}
        onBack={handleBackButton}
        onShare={onShare}
      >
        {/* Mots maîtrisés */}
        {masteredEntries.length > 0 && (
          <div className="summ-word-section">
            <p className="summ-word-section__title">{t('learnMode.summary.masteredCount', { count: masteredEntries.length })}</p>
            {masteredEntries.map(([word]) => (
              <div key={word} className="summ-word-row">
                <span className="summ-word-row__word">{cleanParentheses(word)}</span>
                <span className="summ-word-row__badge summ-word-row__badge--ok">✓</span>
              </div>
            ))}
          </div>
        )}

        {/* Mots à revoir */}
        {reviewEntries.length > 0 && (
          <div className="summ-word-section">
            <p className="summ-word-section__title">{t('learnMode.summary.toReviewCount', { count: reviewEntries.length })}</p>
            {reviewEntries.map(([word, score]) => (
              <div key={word} className="summ-word-row">
                <span className="summ-word-row__word">{cleanParentheses(word)}</span>
                <span className="summ-word-row__translation">{score}/{TESTS_PER_WORD}</span>
                <span className="summ-word-row__badge summ-word-row__badge--miss">↺</span>
              </div>
            ))}
          </div>
        )}
      </SummaryScreen>
    );
  }

  // Interface - Catégorie complète
  if (isLessonComplete) {
    const learnedWords = getCategoryLearnedWords(category);
    const totalLearned = learnedWords.size;

    return (
      <SummaryScreen
        theme={theme}
        label={t('learnMode.summary.categoryDoneLabel')}
        congrats={summaryCongrats}
        stats={[
          { value: `+${totalLearned * XP_PER_WORD}`, label: t('learnMode.summary.xp') },
          { value: `${totalLearned}`, label: t('learnMode.summary.mastered') },
          { value: '100%', label: t('learnMode.summary.completed') },
        ]}
        proverb={summaryProverb}
        primaryBtn={
          onNextCategory
            ? { label: t('learnMode.summary.nextCategory'), onClick: onNextCategory }
            : { label: t('learnMode.summary.backToCategories'), onClick: () => onBackToCategories?.() }
        }
        secondaryBtn={
          onNextCategory
            ? { label: t('learnMode.summary.backToCategories'), onClick: () => onBackToCategories?.() }
            : undefined
        }
        onBack={() => onBackToCategories?.()}
        onShare={onShare}
      />
    );
  }

  // Interface principale - Mode preview et test
  const currentWordData = currentWords[currentWordIndex];
  const testWordData = currentWords.find(([word]) => word === testQueue[0]);
  // Must match the localized value generateStableOptions handed out as the
  // correct option (stableOptions), or the "✓" badge would land on the
  // wrong button once an English translation exists.
  const correctTranslation = testWordData ? getWordTranslation(testWordData[1], i18n.language) : undefined;

  return (
    <div className="lm-screen" data-theme={theme}>
      <StreakAnimation
        show={showWordLearnedAnim}
        type="word-learned"
        onComplete={() => setShowWordLearnedAnim(false)}
      />

      <FloatingBackButton
        onBack={handleBackButton}
        position="top-left"
        variant="minimal"
      />

      {/* Bordure tribale décorative — visible en mode preview uniquement */}
      {mode === "preview" && <TribalBorder />}

      {/* ── PREVIEW MODE ─────────────────────────────── */}
      {mode === "preview" && currentWordData && (
        <div className="lm-preview">
          {/* Top progress bar with counter */}
          <div className="lm-topbar">
            <div className="lm-topbar__track">
              <motion.div
                className="lm-topbar__fill"
                animate={{ width: `${((currentWordIndex + 1) / currentWords.length) * 100}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>
            <span className="lm-topbar__count">{currentWordIndex + 1}/{currentWords.length}</span>
          </div>

          {/* Category badge */}
          <div className="lm-cat-badge">{displayCategoryLabel(category, t)}</div>

          {/* Niche card — illustration en haut, mot, écoute, traduction */}
          <div className="lm-niche">
            {currentWordData[1].illustration && (
              <div className="lm-niche__illustration" title={currentWordData[1].reading}>
                <Illustration value={currentWordData[1].illustration} />
              </div>
            )}
            <h2 className="lm-arch-card__word">
              {cleanParentheses(currentWordData[0])}
            </h2>
            {currentWordData[1].audio && (
              <motion.button
                className="lm-audio-btn"
                onClick={() => playWord(currentWordData[1].audio!)}
                whileTap={{ scale: 0.93 }}
              >
                <Volume2 size={16} />
                <span>{t('learnMode.listenButton')}</span>
              </motion.button>
            )}
            <div className="lm-niche__divider" aria-hidden="true">
              <span className="lm-niche__divider-line" />
              <span className="lm-niche__divider-diamond" />
              <span className="lm-niche__divider-line" />
            </div>
            <p className="lm-arch-card__translation">{getWordTranslation(currentWordData[1], i18n.language)}</p>
            {currentWordData[1].explanation && (
              <p className="lm-arch-card__sub">{getWordExplanation(currentWordData[1], i18n.language)}</p>
            )}
          </div>

          {currentWordData[1].example && (
            <div className="lm-usage-box">
              <div className="lm-usage-box__label">{t('learnMode.usageLabel')}</div>
              <p className="lm-usage-box__text">« {getWordExample(currentWordData[1], i18n.language)} »</p>
            </div>
          )}

          {/* Découpage mot à mot — phrases uniquement (pas de traduction par mot
              dans les données actuelles, donc pas de tap-to-translate). */}
          {cleanParentheses(currentWordData[0]).trim().split(/\s+/).length > 2 && (
            <div className="lm-tokens-box">
              <div className="lm-tokens-box__label">{t('learnMode.wordByWordLabel')}</div>
              <div className="lm-tokens-box__list">
                {cleanParentheses(currentWordData[0]).trim().split(/\s+/).map((tok, i) => (
                  <span className="lm-tokens-box__chip" key={i}>{tok}</span>
                ))}
              </div>
            </div>
          )}

          {/* Next button */}
          <motion.button
            className="lm-next-btn"
            onClick={handleNextWord}
            whileTap={{ scale: 0.97 }}
          >
            {currentWordIndex === currentWords.length - 1 ? t('learnMode.startTest') : t('learnMode.nextWord')}
          </motion.button>
        </div>
      )}

      {/* ── TEST MODE ─────────────────────────────────── */}
      {mode === "test" && (
        <div className="lm-test">
          {/* Single fill progress bar + counter */}
          <div className="lm-topbar">
            <div className="lm-topbar__track">
              <motion.div
                className="lm-topbar__fill"
                animate={{ width: `${Math.max(4, ((currentWords.length * TESTS_PER_WORD - testQueue.length) / (currentWords.length * TESTS_PER_WORD)) * 100)}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>
            <span className="lm-topbar__count">
              {currentWords.length * TESTS_PER_WORD - testQueue.length + 1}/{currentWords.length * TESTS_PER_WORD}
            </span>
          </div>

          {/* Category badge */}
          <div className="lm-cat-badge">{displayCategoryLabel(category, t)}</div>

          {/* Question arch card */}
          {/* Question content */}
          <p className="lm-arch-card__label">{t('learnMode.translateInstruction')}</p>
          <h2 className="lm-arch-card__word">{cleanParentheses(testQueue[0])}</h2>
          {testWordData?.[1].explanation && (
            <p className="lm-arch-card__sub">{getWordExplanation(testWordData[1], i18n.language)}</p>
          )}
          {testWordData?.[1].audio && (
            <motion.button
              className="lm-audio-btn"
              onClick={() => playWord(testWordData[1].audio!)}
              whileTap={{ scale: 0.93 }}
            >
              <Volume2 size={16} />
              <span>{t('learnMode.listenButton')}</span>
            </motion.button>
          )}

          {/* Options grid */}
          <div className="lm-options">
            {stableOptions.map((option, index) => {
              const isSelected = selectedAnswer === option;
              const isCorrectOpt = option === correctTranslation;
              const showCorrect = !!selectedAnswer && isCorrectOpt;
              const showWrong = !!selectedAnswer && isSelected && !isCorrectOpt;
              return (
                <motion.button
                  key={`${option}-${index}`}
                  className={`lm-option${showCorrect ? ' lm-option--correct' : ''}${showWrong ? ' lm-option--wrong' : ''}`}
                  onClick={() => handleAnswer(option)}
                  disabled={!!selectedAnswer}
                  whileTap={!selectedAnswer ? { scale: 0.97 } : undefined}
                >
                  <span className="lm-option__letter">{String.fromCharCode(65 + index)}</span>
                  <span className="lm-option__text">{option}</span>
                  {showCorrect && <span className="lm-option__badge lm-option__badge--ok">✓</span>}
                  {showWrong  && <span className="lm-option__badge lm-option__badge--no">✗</span>}
                </motion.button>
              );
            })}
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showExitConfirmation}
        onConfirm={handleExitConfirm}
        onCancel={() => setShowExitConfirmation(false)}
        message={t('learnMode.exitConfirm.message') as string}
        title={t('learnMode.exitConfirm.title') as string}
        confirmText={t('learnMode.exitConfirm.confirm') as string}
        cancelText={t('learnMode.exitConfirm.cancel') as string}
        confirmButtonStyle="danger"
        position="top"
      />
    </div>
  );
};

export default LearnMode;
